// §v3.test-status — visible "automated testing & deploy health" panel.
// Self-contained + FAILS QUIET (renders nothing on error) so it can never break
// the page it's dropped into. Data from GET /api/admin/test-status.
import { useEffect, useState } from "react";
import { getTestStatus, type TestStatus } from "@/lib/api";

function timeAgo(ts: number): string {
  const s = Math.max(0, Math.floor(Date.now() / 1000) - ts);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function Stat(props: { label: string; value: string; sub?: string; bad?: boolean }) {
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-3.5">
      <div className="text-[11px] uppercase tracking-wide text-white/40">{props.label}</div>
      <div className={`mt-1 text-[18px] font-semibold ${props.bad ? "text-red-300" : "text-white/90"}`}>
        {props.value}
      </div>
      {props.sub ? <div className="mt-0.5 text-[12px] text-white/50">{props.sub}</div> : null}
    </div>
  );
}

export function TestStatusCard() {
  const [data, setData] = useState<TestStatus | null>(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    getTestStatus()
      .then((d) => alive && setData(d))
      .catch(() => alive && setErr(true));
    return () => {
      alive = false;
    };
  }, []);

  if (err) return null; // fail quiet — never break the host page
  const smoke = data?.smoke ?? null;
  const smokeBad = smoke != null && !smoke.ok;

  return (
    <section className="rounded-[28px] border border-white/10 bg-white/[0.03] p-5 sm:p-6">
      <div className="flex items-center gap-2">
        <span className="text-[15px] font-semibold tracking-tight text-white/90">
          Automated testing &amp; deploy health
        </span>
        {data ? (
          <span
            className={`ml-auto text-[12px] font-medium ${
              smokeBad ? "text-red-300" : "text-emerald-300"
            }`}
          >
            {smoke == null ? "—" : smoke.ok ? "✓ healthy" : "✗ check failed"}
          </span>
        ) : null}
      </div>
      <p className="mt-1.5 text-[13px] leading-relaxed text-white/55">
        Every code change is automatically tested before it can reach you. This is the
        testing posture for the build you're running right now.
      </p>

      {!data ? (
        <p className="mt-4 text-[13px] text-white/40">Loading…</p>
      ) : (
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Stat
            label="Automated tests · every push"
            value={data.suite.total.toLocaleString()}
            sub={
              data.suite.frontend == null
                ? `${data.suite.python.toLocaleString()} backend test functions · frontend suite runs in CI`
                : `${data.suite.python.toLocaleString()} backend · ${data.suite.frontend} frontend`
            }
          />
          <Stat
            label="Last deploy health check"
            value={smoke == null ? "Not run yet" : `${smoke.passed}/${smoke.total} passed`}
            sub={
              smoke == null
                ? "Runs automatically on the next deploy"
                : `${smoke.ok ? "✓ all green" : "✗ a path failed"} · ${timeAgo(smoke.ts)}`
            }
            bad={smokeBad}
          />
          <Stat
            label="Deployed build"
            value={data.version.shortSha || "—"}
            sub={`${data.version.branch || "—"} · up ${Math.floor(
              (data.version.uptimeSeconds || 0) / 3600,
            )}h`}
          />
        </div>
      )}
    </section>
  );
}
